import { useDispatch, useSelector } from 'react-redux'
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import boardApi from '../../api/boardApi'
import { setBoards } from '../../redux/features/boardSlice'
import { clearToken } from '../../utils/storage'
import { errorMessage } from '../../utils/errors'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import FavouriteList from './FavouriteList'
import Brand from './Brand'
import { useToast } from './ToastProvider'
import { tokens } from '../../theme'

const sidebarWidth = 264

const Sidebar = ({ mobileOpen, onClose, isDesktop }) => {
  const user = useSelector((state) => state.user.value)
  const boards = useSelector((state) => state.board.value)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const toast = useToast()
  const { boardId } = useParams()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    const getBoards = async () => {
      try {
        const res = await boardApi.getAll()
        if (active) dispatch(setBoards(res))
      } catch (err) {
        if (active) toast.error(errorMessage(err, 'Could not load your boards.'))
      } finally {
        if (active) setLoading(false)
      }
    }
    getBoards()
    return () => { active = false }
  }, [dispatch, toast])

  useEffect(() => {
    if (!loading && boards.length > 0 && boardId === undefined) {
      navigate(`/boards/${boards[0].id}`, { replace: true })
    }
  }, [boards, boardId, loading, navigate])

  const logout = () => {
    clearToken()
    navigate('/login', { replace: true })
  }

  const onDragEnd = async ({ source, destination }) => {
    if (!destination || destination.index === source.index) return

    const previous = boards
    const next = [...boards]
    const [moved] = next.splice(source.index, 1)
    next.splice(destination.index, 0, moved)

    dispatch(setBoards(next))
    try {
      await boardApi.updatePosition({ boards: next })
    } catch (err) {
      dispatch(setBoards(previous))
      toast.error(errorMessage(err, 'Could not save the new order.'))
    }
  }

  const addBoard = async () => {
    try {
      const res = await boardApi.create()
      dispatch(setBoards([res, ...boards]))
      navigate(`/boards/${res.id}`)
      onClose?.()
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the board.'))
    }
  }

  const content = (
    <Box sx={{ width: sidebarWidth, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ px: 2.5, py: 2.5 }}
      >
        <Brand />
        {!isDesktop && (
          <IconButton onClick={onClose} aria-label='Close navigation' size='small'>
            <CloseOutlinedIcon fontSize='small' />
          </IconButton>
        )}
      </Stack>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', pb: 2 }}>
        <FavouriteList onNavigate={onClose} />

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ pl: 2.5, pr: 1.5, mt: 2.5, mb: 0.5 }}
        >
          <Typography variant='overline' color='text.disabled'>
            Boards
          </Typography>
          <Tooltip title='New board'>
            <IconButton size='small' onClick={addBoard} aria-label='New board'>
              <AddOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>

        {loading ? (
          <Stack spacing={1} sx={{ px: 2, pt: 1 }}>
            {[0, 1, 2, 3].map((key) => (
              <Skeleton key={key} variant='rounded' height={32} />
            ))}
          </Stack>
        ) : boards.length === 0 ? (
          <Typography variant='body2' color='text.disabled' sx={{ px: 2.5, pt: 1 }}>
            No boards yet.
          </Typography>
        ) : (
          <List disablePadding>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId='boards-droppable'>
                {(provided) => (
                  <Box ref={provided.innerRef} {...provided.droppableProps}>
                    {boards.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(dragProvided, snapshot) => (
                          <ListItemButton
                            ref={dragProvided.innerRef}
                            {...dragProvided.dragHandleProps}
                            {...dragProvided.draggableProps}
                            selected={item.id === boardId}
                            component={Link}
                            to={`/boards/${item.id}`}
                            onClick={() => onClose?.()}
                            sx={{
                              cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                              boxShadow: snapshot.isDragging ? `0 8px 24px ${tokens.shadow}` : 'none'
                            }}
                          >
                            <Box
                              component='span'
                              sx={{ fontSize: '0.95rem', mr: 1, lineHeight: 1 }}
                              aria-hidden
                            >
                              {item.icon}
                            </Box>
                            <Typography variant='body2' fontWeight={500} noWrap>
                              {item.title || 'Untitled'}
                            </Typography>
                          </ListItemButton>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </List>
        )}
      </Box>

      <Divider />
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ px: 2, py: 1.75 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: '0.8125rem',
            fontWeight: 700,
            backgroundColor: tokens.accentSoft,
            color: 'primary.main'
          }}
        >
          {(user?.username || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant='body2' fontWeight={600} noWrap sx={{ flexGrow: 1 }}>
          {user?.username}
        </Typography>
        <Tooltip title='Log out'>
          <IconButton onClick={logout} size='small' aria-label='Log out'>
            <LogoutOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  )

  const paperSx = {
    width: sidebarWidth,
    borderRight: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'rgba(15, 19, 24, 0.92)',
    backgroundImage: 'none'
  }

  if (isDesktop) {
    return (
      <Drawer
        variant='permanent'
        open
        sx={{ width: sidebarWidth, flexShrink: 0, '& .MuiDrawer-paper': paperSx }}
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant='temporary'
      open={mobileOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{ '& .MuiDrawer-paper': paperSx }}
    >
      {content}
    </Drawer>
  )
}

export default Sidebar
